import { useMediaQuery, useTheme } from '@mui/material';
import { createContext, useCallback, useMemo, useState } from 'react';
import {
  DEFAULT_PROPS,
  FULL_SIZE_MARGIN,
  MIN_HEIGHT,
  MIN_WIDTH,
} from 'src/components/rnd-modal';

// ----------------------------------------------------------------------

export type ModalStateType = {
  token?: string;
  roomName?: string;
  conversationId?: string;
  open: boolean;
  maximize: boolean;
  chat: boolean;
  size: {
    width: number;
    height: number;
  };
  position: {
    x: number;
    y: number;
  };
};

export type ConversationModalContextType = {
  state: ModalStateType;
  onMaximize: () => void;
  onMinimize: () => void;
  onResizeStop: ({ width, height }: { width: number; height: number }) => void;
  onDragStop: ({ x, y }: { x: number; y: number }) => void;
  openModal: ({
    token,
    roomName,
    conversationId,
  }: {
    token?: string;
    roomName?: string;
    conversationId?: string;
  }) => void;
  closeModal: () => void;
};

export const ConversationModalContext =
  createContext<ConversationModalContextType | null>(null);

type ConversationModalProviderProps = {
  children: React.ReactNode;
};

export function ConversationModalProvider({
  children,
}: ConversationModalProviderProps) {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const [state, setState] = useState<ModalStateType>({
    open: false,
    maximize: false,
    chat: false,
    size: {
      width: isDownMd ? MIN_WIDTH : DEFAULT_PROPS.width,
      height: isDownMd ? MIN_HEIGHT : DEFAULT_PROPS.height,
    },
    position: {
      x: DEFAULT_PROPS.x,
      y: DEFAULT_PROPS.y,
    },
  });

  const onMaximize = () => {
    const margin = isDownMd ? 0 : FULL_SIZE_MARGIN;
    setState((state) => ({
      ...state,
      maximize: true,
      size: {
        width: document.body.clientWidth - 2 * margin,
        height: document.body.clientHeight - 2 * margin,
      },
      position: { x: margin, y: margin },
    }));
  };

  const onMinimize = () => {
    setState((state) => ({
      ...state,
      maximize: false,
      size: {
        width: isDownMd ? MIN_WIDTH : DEFAULT_PROPS.width,
        height: isDownMd ? MIN_HEIGHT : DEFAULT_PROPS.height,
      },
      position: {
        x: isDownMd ? 0 : DEFAULT_PROPS.x,
        y: isDownMd ? 0 : DEFAULT_PROPS.y,
      },
    }));
  };

  const onResizeStop = ({
    width,
    height,
  }: {
    width: number;
    height: number;
  }) => {
    setState((state) => ({
      ...state,
      maximize: false,
      size: {
        width,
        height,
      },
    }));
  };

  const onDragStop = ({ x, y }: { x: number; y: number }) => {
    setState((state) => ({
      ...state,
      maximize: false,
      position: {
        x,
        y,
      },
    }));
  };

  const openModal = useCallback(
    ({
      token,
      roomName,
      conversationId,
    }: {
      token?: string;
      roomName?: string;
      conversationId?: string;
    }) => {
      setState((state) => ({
        ...state,
        open: true,
        roomName,
        token,
        conversationId,
      }));
    },
    [],
  );

  const closeModal = () =>
    setState((state) => ({
      maximize: false,
      chat: false,
      size: {
        width: isDownMd ? MIN_WIDTH : DEFAULT_PROPS.width,
        height: isDownMd ? MIN_HEIGHT : DEFAULT_PROPS.height,
      },
      position: {
        x: DEFAULT_PROPS.x,
        y: DEFAULT_PROPS.y,
      },
      open: false,
      roomName: undefined,
      token: undefined,
    }));

  const memoizedValue = useMemo(
    () => ({
      state,
      onMaximize,
      onMinimize,
      onResizeStop,
      onDragStop,
      openModal,
      closeModal,
    }),
    [state, openModal],
  );

  return (
    <ConversationModalContext.Provider value={memoizedValue}>
      {children}
    </ConversationModalContext.Provider>
  );
}
